
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Header from "@/components/Header";
import { UserPlus } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";

const Signup = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const { signUp, signInWithProvider } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  // Password strength validation
  const validatePassword = (pass: string): { valid: boolean; message: string } => {
    if (pass.length < 8) {
      return { valid: false, message: "Password must be at least 8 characters" };
    }
    
    const hasLowerCase = /[a-z]/.test(pass);
    const hasUpperCase = /[A-Z]/.test(pass);
    const hasNumber = /\d/.test(pass);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(pass);
    
    if (!(hasLowerCase && hasUpperCase && hasNumber)) {
      return { 
        valid: false, 
        message: "Password must contain at least one lowercase letter, one uppercase letter, and one number" 
      };
    }
    
    if (!hasSpecial) {
      return { 
        valid: false, 
        message: "Password should include at least one special character" 
      };
    }
    
    return { valid: true, message: "Password is strong" };
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password || !confirmPassword || !fullName) {
      toast({
        title: t("common.error", language),
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }
    
    if (password !== confirmPassword) {
      toast({
        title: t("common.error", language),
        description: t("signup.passwordMismatch", language),
        variant: "destructive",
      });
      return;
    }
    
    const passwordCheck = validatePassword(password);
    if (!passwordCheck.valid) {
      toast({
        title: t("common.error", language),
        description: passwordCheck.message,
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      await signUp(email, password, fullName);
      toast({
        title: t("common.success", language) || "Success!",
        description: "Your account has been created. Please check your email for verification.",
      });
      navigate("/login");
    } catch (error) {
      console.error("Signup error:", error);
      toast({
        title: t("common.error", language),
        description: error instanceof Error ? error.message : t("signup.errorMessage", language),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true);
    try {
      await signInWithProvider('google');
    } catch (error) {
      console.error("Google signup error:", error);
      toast({
        title: t("common.error", language),
        description: error instanceof Error ? error.message : "Failed to sign up with Google",
        variant: "destructive",
      });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <main className="min-h-screen pt-24 pb-16">
      <Header />
      <div className="container max-w-md mx-auto px-4">
        <div className="mb-8 text-center animate-fade-down">
          <UserPlus className="mx-auto h-12 w-12 text-primary mb-4" />
          <h1 className="text-3xl font-bold tracking-tight">{t('signup.title', language)}</h1>
          <p className="text-muted-foreground mt-1">
            {t('signup.subtitle', language)}
          </p>
        </div>

        <Button 
          variant="outline" 
          className="w-full flex items-center justify-center gap-2 mb-4"
          onClick={handleGoogleSignUp}
          disabled={isGoogleLoading}
        >
          {isGoogleLoading ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <svg width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
              <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
              <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
              <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
            </svg>
          )}
          {isGoogleLoading ? t('login.signingIn', language) || "Signing in..." : t('auth.googleLogin', language)}
        </Button>

        <div className="relative my-6">
          <Separator />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-2 text-xs text-muted-foreground">
            {t('login.or', language) || "OR"}
          </span>
        </div>

        <form onSubmit={handleSignUp} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full-name">{t('signup.form.name', language)}</Label>
            <Input
              id="full-name"
              type="text"
              placeholder="Your Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">{t('signup.form.email', language)}</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">{t('signup.form.password', language)}</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Must be at least 8 characters with a mix of uppercase, lowercase, numbers, and special characters.
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirm-password">{t('signup.form.confirmPassword', language)}</Label>
            <Input
              id="confirm-password"
              type="password"
              placeholder="••••••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>
          
          <Button 
            type="submit" 
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? 
              "Creating Account..." : 
              t('signup.form.signup', language)
            }
          </Button>
          
          <div className="text-center mt-4">
            <p className="text-sm text-muted-foreground">
              {t('signup.alreadyHaveAccount', language)}{" "}
              <Link 
                to="/login" 
                className="font-medium text-primary hover:underline"
              >
                {t('signup.login', language)}
              </Link>
            </p>
          </div>
        </form>
      </div>
    </main>
  );
};

export default Signup;
